import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { getGlobalContentitem } from '@/helpers/contentHelpers';
import { Link } from 'react-router-dom';
import '@/css/cookieconsent.css';

// --- Types
export type ConsentCategories = {
    necessary: true;
    analytics: boolean;
    marketing: boolean;
    preferences: boolean;
};

export type ConsentState = {
    categories: ConsentCategories;
    version: number;
    updatedAt: string;
};

// --- Constants
const STORAGE_KEY = "cookie-consent:v1";
const CURRENT_VERSION = 1;

const DEFAULT_CONSENT: ConsentState = {
    version: CURRENT_VERSION,
    updatedAt: new Date(0).toISOString(),
    categories: {
        necessary: true,
        analytics: false,
        marketing: false,
        preferences: false,
    },
};

// --- Storage helpers
function loadConsent(): ConsentState | null {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) return null;
        return JSON.parse(raw) as ConsentState;
    } catch {
        return null;
    }
}

function saveConsent(state: ConsentState) {
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch {
        // ignore
    }
}

// --- Global registry for category listeners
const listeners: Record<keyof ConsentCategories, Set<(allowed: boolean) => void>> = {
    necessary: new Set(),
    analytics: new Set(),
    marketing: new Set(),
    preferences: new Set(),
};

export function onConsent(
    category: keyof ConsentCategories,
    callback: (allowed: boolean) => void
): () => void {
    listeners[category].add(callback);
    return () => listeners[category].delete(callback);
}

function emitConsent(category: keyof ConsentCategories, allowed: boolean) {
    listeners[category].forEach((cb) => {
        try {
            cb(allowed);
        } catch {
            // ignore
        }
    });
}

export function injectScriptOnce({ id, src, async = true, defer = true }: { id: string; src: string; async?: boolean; defer?: boolean; }) {
    if (document.getElementById(id)) return;
    const s = document.createElement("script");
    s.id = id;
    s.src = src;
    s.async = async;
    s.defer = defer;
    document.head.appendChild(s);
}

export function removeScriptById(id: string) {
    const existing = document.getElementById(id);
    if (existing) existing.remove();
}

// --- Context
type ConsentContextType = {
    consent: ConsentState;
    setConsent: (next: Partial<ConsentCategories> | ((prev: ConsentCategories) => Partial<ConsentCategories>)) => void;
    isBannerVisible: boolean;
    openPreferences: () => void;
    closePreferences: () => void;
    globalState: any;
};

const ConsentContext = createContext<ConsentContextType | null>(null);

export function useConsent() {
    const ctx = useContext(ConsentContext);
    if (!ctx) throw new Error("useConsent must be used inside <CookieConsentProvider>");
    return ctx;
}

// --- Provider
export const CookieConsentProvider: React.FC<{ children: React.ReactNode; globalState?: any; }> = ({ children, globalState }) => {
    const [consent, setConsentState] = useState<ConsentState>(() => loadConsent() || DEFAULT_CONSENT);
    const [isBannerVisible, setBannerVisible] = useState<boolean>(() => !loadConsent());
    const [prefsOpen, setPrefsOpen] = useState(false);

    useEffect(() => {
        const stored = loadConsent();
        if (stored) {
            (Object.keys(consent.categories) as (keyof ConsentCategories)[]).forEach((k) => {
                const prev = stored.categories[k];
                const next = consent.categories[k];
                if (prev !== next) emitConsent(k, next);
            });
        } else {
            (Object.keys(consent.categories) as (keyof ConsentCategories)[]).forEach((k) => emitConsent(k, consent.categories[k]));
        }
        saveConsent(consent);
    }, [consent]);

    const setConsent = (next: Partial<ConsentCategories> | ((prev: ConsentCategories) => Partial<ConsentCategories>)) => {
        setConsentState((prev) => {
            const patch = typeof next === "function" ? next(prev.categories) : next;
            const merged: ConsentState = {
                version: CURRENT_VERSION,
                updatedAt: new Date().toISOString(),
                categories: {
                    ...prev.categories,
                    ...patch,
                    necessary: true,
                },
            };
            return merged;
        });
    };

    const value = useMemo<ConsentContextType>(
        () => ({ 
            consent, 
            setConsent, 
            isBannerVisible, 
            openPreferences: () => setPrefsOpen(true), 
            closePreferences: () => setPrefsOpen(false), 
            globalState 
        }),
        [consent, isBannerVisible, globalState]
    );

    return (
        <ConsentContext.Provider value={value}>
            {children}
            {isBannerVisible && (
                <CookieBanner
                    onAcceptAll={() => {
                        setConsent({ analytics: true, marketing: true, preferences: true });
                        setBannerVisible(false);
                    }}
                    onRejectAll={() => {
                        setConsent({ analytics: false, marketing: false, preferences: false });
                        setBannerVisible(false);
                    }}
                    onPreferences={() => setPrefsOpen(true)}
                />
            )}
            {prefsOpen && (
                <PreferencesDialog
                    consent={consent.categories}
                    onClose={() => setPrefsOpen(false)}
                    onSave={(cats) => {
                        setConsent(cats);
                        setPrefsOpen(false);
                        setBannerVisible(false);
                    }}
                />
            )}
        </ConsentContext.Provider>
    );
};

const CookieBanner: React.FC<{
    onAcceptAll: () => void;
    onRejectAll: () => void;
    onPreferences: () => void;
}> = ({ onAcceptAll, onRejectAll, onPreferences }) => {
    const { globalState } = useConsent();

    const bannerTitle = getGlobalContentitem("cookie.banner.title", globalState)?.content || "Cookies";
    const bannerText = getGlobalContentitem("cookie.banner.text", globalState)?.content || "We gebruiken cookies.";
    const readMore = getGlobalContentitem("cookie.banner.readMore", globalState)?.content || "Lees meer";
    const btnRejectAll = getGlobalContentitem("cookie.banner.btnRejectAll", globalState)?.content || "Alleen noodzakelijk";
    const btnPreferences = getGlobalContentitem("cookie.banner.btnPreferences", globalState)?.content || "Voorkeuren";
    const btnAcceptAll = getGlobalContentitem("cookie.banner.btnAcceptAll", globalState)?.content || "Alles accepteren";

    return (
        <div className="cc-root" role="dialog" aria-live="polite" aria-label="Cookie-toestemming">
            <div className="cc-card">
                <div className="cc-content">
                    <h2 className="cc-title">{bannerTitle}</h2>
                    <p className="cc-text">{bannerText}</p>
                    <a className="cc-link" href="/privacy" target="_blank" rel="noopener noreferrer">{readMore}</a>
                </div>
                <div className="cc-actions">
                    <button className="cc-btn cc-btn-secondary" onClick={onRejectAll}>{btnRejectAll}</button>
                    <button className="cc-btn cc-btn-outline" onClick={onPreferences}>{btnPreferences}</button>
                    <button className="cc-btn cc-btn-primary" onClick={onAcceptAll}>{btnAcceptAll}</button>
                </div>
            </div>
        </div>
    );
};

const PreferencesDialog: React.FC<{
    consent: ConsentCategories;
    onSave: (cats: Partial<ConsentCategories>) => void;
    onClose: () => void;
}> = ({ consent, onSave, onClose }) => {
    const { globalState } = useConsent();
    const [local, setLocal] = useState<ConsentCategories>(consent);

    useEffect(() => setLocal(consent), [consent]);

    const modalTitle = getGlobalContentitem("cookie.modal.title", globalState)?.content || "Cookie-voorkeuren";
    const catNecessaryTitle = getGlobalContentitem("cookie.cat.necessary.title", globalState)?.content || "Noodzakelijk";
    const catNecessaryDesc = getGlobalContentitem("cookie.cat.necessary.desc", globalState)?.content || "Voor basisfunctionaliteit.";
    const catPreferencesTitle = getGlobalContentitem("cookie.cat.preferences.title", globalState)?.content || "Voorkeuren";
    const catPreferencesDesc = getGlobalContentitem("cookie.cat.preferences.desc", globalState)?.content || "Onthoudt jouw keuzes.";
    const catAnalyticsTitle = getGlobalContentitem("cookie.cat.analytics.title", globalState)?.content || "Analytics";
    const catAnalyticsDesc = getGlobalContentitem("cookie.cat.analytics.desc", globalState)?.content || "Helpt ons de prestaties te begrijpen.";
    const catMarketingTitle = getGlobalContentitem("cookie.cat.marketing.title", globalState)?.content || "Marketing";
    const catMarketingDesc = getGlobalContentitem("cookie.cat.marketing.desc", globalState)?.content || "Voor gepersonaliseerde content.";
    const btnCancel = getGlobalContentitem("cookie.modal.btnCancel", globalState)?.content || "Annuleren";
    const btnSave = getGlobalContentitem("cookie.modal.btnSave", globalState)?.content || "Opslaan";

    return (
        <div className="cc-modal" role="dialog" aria-modal="true" aria-label="Cookie-voorkeuren">
            <div className="cc-modal-card">
                <div className="cc-modal-head">
                    <h3 className="cc-title">{modalTitle}</h3>
                    <button className="cc-close" onClick={onClose} aria-label="Sluiten">
                        <i className="fa-solid fa-xmark" aria-hidden="true"></i>
                    </button>
                </div>
                <div className="cc-modal-body">
                    <CategoryRow
                        title={catNecessaryTitle}
                        description={catNecessaryDesc}
                        checked
                        disabled
                    />
                    <CategoryRow
                        title={catPreferencesTitle}
                        description={catPreferencesDesc}
                        checked={local.preferences}
                        onChange={(v) => setLocal((p) => ({ ...p, preferences: v }))}
                    />
                    <CategoryRow
                        title={catAnalyticsTitle}
                        description={catAnalyticsDesc}
                        checked={local.analytics}
                        onChange={(v) => setLocal((p) => ({ ...p, analytics: v }))}
                    />
                    <CategoryRow
                        title={catMarketingTitle}
                        description={catMarketingDesc}
                        checked={local.marketing}
                        onChange={(v) => setLocal((p) => ({ ...p, marketing: v }))}
                    />
                </div>
                <div className="cc-modal-actions">
                    <button className="cc-btn cc-btn-secondary" onClick={onClose}>{btnCancel}</button>
                    <button className="cc-btn cc-btn-primary" onClick={() => onSave(local)}>{btnSave}</button>
                </div>
            </div>
        </div>
    );
};

const CategoryRow: React.FC<{
    title: string;
    description: string;
    checked?: boolean;
    disabled?: boolean;
    onChange?: (checked: boolean) => void;
}> = ({ title, description, checked, disabled, onChange }) => (
    <label className={`cc-row ${disabled ? "cc-row-disabled" : ""}`}>
        <div className="cc-row-main">
            <span className="cc-row-title">{title}</span>
            <span className="cc-row-desc">{description}</span>
        </div>
        <input
            type="checkbox"
            className="cc-toggle"
            checked={!!checked}
            disabled={disabled}
            onChange={(e) => onChange?.(e.currentTarget.checked)}
        />
    </label>
);

export function setupGTM(gtmId: string) {
    const unsub = onConsent("analytics", (allowed) => {
        if (allowed) {
            const src = `https://www.googletagmanager.com/gtm.js?id=${gtmId}`;
            injectScriptOnce({ id: "gtm-script", src });
        } else {
            removeScriptById("gtm-script");
        }
    });
    return unsub;
}

export const CookiePreferencesLink: React.FC<{ className?: string; label?: string }> = ({ className, label }) => {
    const { openPreferences, globalState } = useConsent();
    const defaultLabel = getGlobalContentitem("cookie.link.preferences", globalState)?.content || "Cookie-voorkeuren";

    return (
        <Link 
            to="cookiepreferences" 
            className={className} 
            onClick={e => { e.preventDefault(); openPreferences(); }} 
            type="button"
        >
            {label ?? defaultLabel}
        </Link>
    );
};
